var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { User } from 'lucide-react';
import { useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './avatar.jsx';
import { cn } from '../../lib/utils.js';
/** Avatar fallback color palette. Each string contains the bg and text Tailwind classes for light and dark mode. */
var AVATAR_COLORS = [
    'bg-amber-200 dark:bg-amber-900',
    'bg-green-200 dark:bg-green-900',
    'bg-blue-200 dark:bg-blue-900',
    'bg-violet-200 dark:bg-violet-900',
    'bg-rose-200 dark:bg-rose-900',
    'bg-orange-200 dark:bg-orange-900',
    'bg-teal-200 dark:bg-teal-900',
    'bg-purple-200 dark:bg-purple-900',
    'bg-cyan-200 dark:bg-cyan-900',
    'bg-fuchsia-200 dark:bg-fuchsia-900',
    'bg-pink-200 dark:bg-pink-900',
    'bg-sky-200 dark:bg-sky-900',
    'bg-indigo-200 dark:bg-indigo-900',
    'bg-lime-200 dark:bg-lime-900',
];
/**
 * Returns a deterministic avatar color class for a given seed string.
 * The same seed always produces the same class, ensuring consistent avatar colors.
 */
var fallbackColor = function (seed) {
    var _a;
    var fallback = 'bg-muted text-foreground';
    if (!seed)
        return fallback;
    var hash = 0;
    for (var i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    }
    return (_a = AVATAR_COLORS[hash % AVATAR_COLORS.length]) !== null && _a !== void 0 ? _a : fallback;
};
/**
 * A reusable avatar component for displaying user avatars with fallback support
 */
export function UserAvatar(_a) {
    var avatarUrl = _a.avatarUrl, firstName = _a.firstName, lastName = _a.lastName, fallbackInitials = _a.fallbackInitials, _b = _a.showUserIconFallback, showUserIconFallback = _b === void 0 ? false : _b, className = _a.className, avatarFallbackClassName = _a.avatarFallbackClassName, userIconFallbackClassName = _a.userIconFallbackClassName, props = __rest(_a, ["avatarUrl", "firstName", "lastName", "fallbackInitials", "showUserIconFallback", "className", "avatarFallbackClassName", "userIconFallbackClassName"]);
    var initials = useMemo(function () {
        var _a, _b;
        if (fallbackInitials)
            return fallbackInitials.replace(/[^a-zA-Z]/g, '').toUpperCase();
        var firstInitial = (_a = firstName === null || firstName === void 0 ? void 0 : firstName[0]) !== null && _a !== void 0 ? _a : '';
        var lastInitial = (_b = lastName === null || lastName === void 0 ? void 0 : lastName[0]) !== null && _b !== void 0 ? _b : '';
        var first = /[a-zA-Z]/.test(firstInitial) ? firstInitial.toUpperCase() : '';
        var last = /[a-zA-Z]/.test(lastInitial) ? lastInitial.toUpperCase() : '';
        return "".concat(first).concat(last);
    }, [fallbackInitials, firstName, lastName]);
    var fullName = useMemo(function () {
        return [firstName, lastName].filter(Boolean).join(' ') || 'Unknown User';
    }, [firstName, lastName]);
    var colorClass = useMemo(function () { return fallbackColor(initials || fullName); }, [initials, fullName]);
    return (<Avatar className={className} {...props}>
      <AvatarImage className="object-cover" src={avatarUrl || undefined} alt={fullName}/>
      <AvatarFallback className={cn('text-foreground font-medium', props.size === 'sm' ? 'text-xs' : 'text-sm', props.size === 'lg' && 'text-base', colorClass, avatarFallbackClassName)}>
        {showUserIconFallback || !initials ? (<User className={cn('size-4 group-data-[size=sm]/avatar:size-3', userIconFallbackClassName)}/>) : (initials)}
      </AvatarFallback>
    </Avatar>);
}
